import type { CompleteSuccessStory } from '../../types';
import { BarChart3, Zap, Settings, Users, Quote } from 'lucide-react';

interface SolutionSectionProps {
  story: CompleteSuccessStory;
}

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  chart: BarChart3,
  zap: Zap,
  settings: Settings,
  users: Users,
};

export function SolutionSection({ story }: SolutionSectionProps) {
  const solutionQuotes = story.customerQuotes.filter((q) => q.section === 'solution');

  return (
    <section className="bg-gradient-to-br from-card to-background py-20 lg:py-32">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-accent font-mono text-sm uppercase tracking-wider font-semibold">
            The Solution
          </span>
          <h2 className="font-bold text-4xl lg:text-5xl mt-4 mb-6 text-primary">
            Why They Chose Planisware
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            {story.whyChosenText}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {story.differentiators.map((diff, index) => {
            const IconComponent = diff.icon ? iconMap[diff.icon] || BarChart3 : BarChart3;
            return (
              <div
                key={diff.id}
                className="bg-card rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 border border-border"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center mb-6">
                  <IconComponent className="w-7 h-7 text-primary-foreground" />
                </div>
                <h3 className="font-bold text-xl mb-3">{diff.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{diff.description}</p>
              </div>
            );
          })}
        </div>

        {solutionQuotes.length > 0 && (
          <div className="mt-16 bg-white rounded-2xl p-8 lg:p-12 shadow-xl border border-border">
            <div className="flex items-start gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-gradient-to-br from-accent to-accent/80 rounded-full flex items-center justify-center">
                  <Quote className="w-8 h-8 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <p className="text-xl lg:text-2xl text-foreground leading-relaxed italic mb-4">
                  {solutionQuotes[0].quoteText}
                </p>
                <div className="flex items-center gap-4">
                  <div>
                    <p className="font-bold text-lg">{solutionQuotes[0].authorName}</p>
                    <p className="text-muted-foreground text-sm">{solutionQuotes[0].authorTitle}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
